import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/dentconnect/src/components/ui/dialog";
import { Input } from "@/dentlab/src/components/ui/input";
import { Button } from "@/dentlab/src/components/ui/button";
import { Check, X, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { showNotification } from "@/dentlab/src/store/Central/selectors";

interface SetPasswordDialogProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const SetPasswordDialog: React.FC<SetPasswordDialogProps> = ({ open, onClose, onSuccess }) => {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [showPasswordError, setShowPasswordError] = useState(false);

	// Password validation requirements
	const hasMinLength = password.length >= 8;
	const hasUpperCase = /[A-Z]/.test(password);
	const hasLowerCase = /[a-z]/.test(password);
	const hasNumber = /[0-9]/.test(password);
	const passwordsMatch = password === confirmPassword && password.length > 0;
	const isValidPassword = hasMinLength && hasUpperCase && hasLowerCase && hasNumber;

	const handleSubmit = async () => {
		if (!isValidPassword || !passwordsMatch) {
			setShowPasswordError(true);
			return;
		}

		setIsLoading(true);
		try {
			const { error } = await supabase.auth.updateUser({
				password,
				// maps to the auth.users.raw_user_meta_data
				data: { hasPassword: true }
			});
			
			if (error) {
				showNotification({
					message: "Fehler beim Setzen des Passworts. Bitte versuchen Sie es erneut.",
					type: "error",
				});
			} else {
				showNotification({
					message: "Passwort erfolgreich gesetzt",
					type: "success",
				});
				onSuccess?.();
				handleClose();
			}
		} catch (error) {
			showNotification({
				message: "Ein unerwarteter Fehler ist aufgetreten",
				type: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setPassword("");
		setConfirmPassword("");
		setShowPasswordError(false);
		onClose();
	};

	const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
		<div className="flex items-center gap-2 text-sm">
			{met ? (
				<Check className="h-4 w-4 text-green-600" />
			) : (
				<X className="h-4 w-4 text-gray-400" />
			)}
			<span className={met ? "text-green-600" : "text-gray-500"}>{text}</span>
		</div>
	);

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Passwort setzen</DialogTitle>
					<DialogDescription>
						Setzen Sie ein Passwort für Ihren Account. Danach können sich wahlweise mit Passwort oder Einmalpasswort anmelden.
					</DialogDescription>
				</DialogHeader>
				
				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<label htmlFor="password" className="text-sm font-medium">
							Neues Passwort
						</label>
						<Input
							id="password"
							type="password"
							value={password}
							onChange={(e) => {
								setPassword(e.target.value);
								setShowPasswordError(false);
							}}
							placeholder="Passwort eingeben"
						/>
					</div>

					<div className="space-y-2">
						<label htmlFor="confirmPassword" className="text-sm font-medium">
							Passwort bestätigen
						</label>
						<Input
							id="confirmPassword"
							type="password"
							value={confirmPassword}
							onChange={(e) => {
								setConfirmPassword(e.target.value);
								setShowPasswordError(false);
							}}
							placeholder="Passwort wiederholen"
						/>
					</div>

					{/* Password Requirements */}
					<div className="space-y-2 bg-gray-50 p-3 rounded-md">
						<p className="text-sm font-medium text-gray-700">Passwort-Anforderungen:</p>
						<div className="space-y-1">
							<RequirementItem met={hasMinLength} text="Mindestens 8 Zeichen" />
							<RequirementItem met={hasUpperCase} text="Mindestens ein Grossbuchstabe" />
							<RequirementItem met={hasLowerCase} text="Mindestens ein Kleinbuchstabe" />
							<RequirementItem met={hasNumber} text="Mindestens eine Zahl" />
							<RequirementItem met={passwordsMatch} text="Passwörter stimmen überein" />
						</div>
					</div>

					{showPasswordError && (!isValidPassword || !passwordsMatch) && (
						<div className="flex items-center gap-2 text-sm text-red-600">
							<AlertCircle className="h-4 w-4" />
							<span>Bitte erfüllen Sie alle Passwort-Anforderungen</span>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose} disabled={isLoading}>
						Abbrechen
					</Button>
					<Button 
						onClick={handleSubmit} 
						disabled={isLoading || !isValidPassword || !passwordsMatch}
					>
						{isLoading ? "Wird gespeichert..." : "Passwort setzen"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};